import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import useAsyncEffect from 'use-async-effect';
import moment from 'moment';
import { FiArrowLeft, FiSliders, FiExternalLink, FiSearch, FiEdit2, FiTrash2, FiHelpCircle } from 'react-icons/fi';
import { IGlobalState } from '../../../model/IGlobalState';
import { CommunicationsQuestionsResult, InvestorQuestion } from '../../../model/CommunicationsQuestionsResult';
import api from '../../../api/api';
import { to } from '../../../util/util';
import Dropdown from '../Dropdown';
import Toggle from '../Toggle';
import { toast } from '../Toast';
import { QuestionSettingsModal } from './InvestorQuestionModals';

type Sort = 'newest' | 'oldest';
const SORTS = [{ value: 'newest', label: 'Newest first' }, { value: 'oldest', label: 'Oldest first' }];
const published = (q: InvestorQuestion) => moment(q.responseDate || q.created);

// spec 423:16048, /communications/investor-questions/published. Rows = answered + IsPublic questions from the same
// GET as the inbox. Omitted: topics (no source), views (not tracked), drag to reorder. Unpublish / delete are preview only.
const PublishedAnswers: React.FC = () => {
    const history = useHistory();
    const stock = useSelector((state: IGlobalState) => state.currentStock);
    const [result, setResult] = useState<CommunicationsQuestionsResult | null>(null);
    const [error, setError] = useState('');
    const [query, setQuery] = useState('');
    const [sort, setSort] = useState<Sort>('newest');
    const [settings, setSettings] = useState(false);

    useAsyncEffect(async isMounted => {
        if (!stock?.id) return;
        setResult(null);
        const [err, data] = await to(api().getCommunicationsQuestions(stock.id));
        if (!isMounted()) return;
        if (err) setError(err.response?.status === 403 ? 'You need the Reply To Comment role to see investor questions.' : err.message || 'Failed to load questions');
        else { setError(''); setResult(data as CommunicationsQuestionsResult); }
    }, [stock?.id]);

    const all = useMemo(() => (result?.questions ?? []).filter(x => x.answered && x.isPublic), [result]);
    const rows = useMemo(() => {
        const q = query.trim().toLowerCase();
        const list = all.filter(x => !q || `${x.comment} ${x.response} ${x.announcementHeadline}`.toLowerCase().includes(q));
        return list.sort((a, b) => sort === 'newest' ? published(b).diff(published(a)) : published(a).diff(published(b)));
    }, [all, query, sort]);

    const back = <button type="button" className="link pa-back" onClick={() => history.push('/communications/investor-questions')}><FiArrowLeft /> Back to Investor Questions</button>;

    return <div className="published-answers">
        {back}
        <div className="page-header">
            <div>
                <h1>Published Answers</h1>
                <p>Answers you’ve chosen to show publicly under the announcement they were asked on</p>
            </div>
            <div className="actions">
                <button type="button" className="btn-outline" onClick={() => setSettings(true)}><FiSliders /> Question Settings</button>
                {result?.websiteUrl && <a className="btn-outline" href={result.websiteUrl} target="_blank" rel="noopener noreferrer"><FiExternalLink /> View on Website</a>}
            </div>
        </div>

        {error && <p className="error">{error}</p>}
        {!error && !result && <p className="loading">Loading published answers…</p>}

        {result && <div className="card pa-card">
            <div className="pa-filters">
                <label className="pa-search"><FiSearch /><input placeholder="Search questions" value={query} onChange={e => setQuery(e.target.value)} /></label>
                <span className="pa-all">All <em>{all.length}</em></span>
                <div className="pa-sort"><Dropdown value={sort} options={SORTS} onChange={v => setSort(v as Sort)} /></div>
            </div>

            <div className="pa-table">
                <div className="pa-row pa-head">
                    <span>Question</span><span>Announcement</span><span>Published</span><span>Views</span><span>On website</span><span />
                </div>
                {rows.map(q => <div key={q.guid} className="pa-row">
                    <span className="pa-q" title={q.response || ''}>{q.comment}</span>
                    <span className="pa-ann">{q.announcementHeadline ? <span className="pill">{q.announcementHeadline}</span> : '—'}</span>
                    <span className="pa-date">{published(q).format('D MMM YYYY')}</span>
                    <span className="pa-views" title="Views aren’t tracked yet">—</span>
                    <span><Toggle checked label="On website" onChange={() => toast.info('Preview only: unpublishing isn’t connected yet, so the answer is still on the website')} /></span>
                    <span className="pa-acts">
                        <button type="button" className="pa-icon" aria-label="Edit answer" title="Open in Investor Questions"
                            onClick={() => history.push(`/communications/investor-questions/${q.guid}`)}><FiEdit2 /></button>
                        <button type="button" className="pa-icon danger" aria-label="Delete answer"
                            onClick={() => toast.info('Preview only: deleting isn’t connected yet, so the answer was not removed')}><FiTrash2 /></button>
                    </span>
                </div>)}
                {rows.length === 0 && <p className="pa-empty">{query ? 'No published answers match your search.' : 'No answers have been published yet.'}</p>}
            </div>
        </div>}

        <div className="pa-note">
            <FiHelpCircle />
            <span>Published answers show under the announcement they were asked on, with the asker’s name. Answers sent privately never appear here.</span>
        </div>

        {settings && <QuestionSettingsModal onClose={() => setSettings(false)} />}
    </div>;
};

export default PublishedAnswers;
